import type { PartnerAccountSummary, PartnerUser } from '@upweo/types';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { restoreSession, setAccessToken, setActivePartnerAccount } from './api-client.js';
import * as api from './api.js';
import { type PartnerState, PartnerContext } from './partner-context.js';

export const PartnerProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [user, setUser] = useState<PartnerUser | null>(null);
  const [activeAccount, setActiveAccount] = useState<PartnerAccountSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const adopt = useCallback((next: PartnerUser) => {
    setUser(next);
    const first = next.accounts[0] ?? null;
    setActiveAccount(first);
    setActivePartnerAccount(first?.id ?? null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (await restoreSession()) {
          const { user: restored } = await api.me();
          if (!cancelled) {
            adopt(restored);
          }
        }
      } catch {
        // No cookie, or not a partner account — remain signed out.
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adopt]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await api.login({ email, password });
      setAccessToken(result.accessToken);
      adopt(result.user);
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setAccessToken(null);
      setActivePartnerAccount(null);
      setUser(null);
      setActiveAccount(null);
    }
  }, []);

  const selectAccount = useCallback(
    (accountId: string) => {
      const next = user?.accounts.find((a) => a.id === accountId);
      if (!next) {
        return;
      }
      setActivePartnerAccount(next.id);
      setActiveAccount(next);
    },
    [user],
  );

  const value = useMemo<PartnerState>(
    () => ({ user, activeAccount, isLoading, signIn, signOut, selectAccount }),
    [user, activeAccount, isLoading, signIn, signOut, selectAccount],
  );

  return <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>;
};
